import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Newspaper, Sparkles, Copy, Download, Trash2, ImageIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { AssistantChat } from "../components/AssistantChat";
import { downloadMedia } from "../components/ManualDownloadPanel";
import { errMsg } from "../lib/errMsg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LENGTHS = [
  { id: "short", label: "Short", blurb: "~300 words" },
  { id: "medium", label: "Medium", blurb: "~700 words" },
  { id: "long", label: "Long", blurb: "~1300 words" },
];
const TONES = [
  { id: "professional", label: "Professional" },
  { id: "casual", label: "Casual" },
  { id: "inspiring", label: "Inspiring" },
];

// Minimal markdown renderer — headings, bold, paragraphs. No external dep.
const MarkdownBody = ({ text }) => (
  <div className="space-y-3">
    {text.split(/\n{2,}/).map((block, i) => {
      const t = block.trim();
      if (!t) return null;
      if (t.startsWith("## ")) {
        return <h3 key={i} className="text-base font-black text-ink-950 mt-4">{t.slice(3)}</h3>;
      }
      if (t.startsWith("# ")) {
        return <h2 key={i} className="text-lg font-black text-ink-950 mt-4">{t.slice(2)}</h2>;
      }
      const parts = t.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} className="text-sm text-ink-700 leading-relaxed whitespace-pre-wrap">
          {parts.map((p, j) =>
            p.startsWith("**") && p.endsWith("**")
              ? <strong key={j} className="text-ink-950">{p.slice(2, -2)}</strong>
              : p
          )}
        </p>
      );
    })}
  </div>
);

const ArticleCard = ({ article, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  const copyArticle = async () => {
    try {
      await navigator.clipboard.writeText(`# ${article.title}\n\n${article.body}`);
      toast.success("Article copied to clipboard");
    } catch (_e) {
      toast.error("Could not copy");
    }
  };

  const downloadArticle = () => {
    const blob = new Blob([`# ${article.title}\n\n${article.body}`], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${article.title.replace(/[^\w-]+/g, "_").slice(0, 50)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="bg-white border border-ink-200 rounded-3xl overflow-hidden"
      data-testid={`article-card-${article.id}`}
    >
      {article.hero_image_url && (
        <img src={article.hero_image_url} alt="" className="w-full max-h-64 object-cover" />
      )}
      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-black text-ink-950 leading-tight">{article.title}</h2>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-400 mt-1">
              {article.tone} · {article.length} · {new Date(article.created_at).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={() => onDelete(article.id)}
            className="p-2 rounded-full text-ink-400 hover:text-rose-500 hover:bg-rose-50 shrink-0"
            data-testid={`article-delete-${article.id}`}
            aria-label="Delete article"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        <div className={expanded ? "" : "max-h-40 overflow-hidden relative"}>
          <MarkdownBody text={article.body} />
          {!expanded && (
            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
          )}
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs font-bold text-violet-600 hover:underline"
          data-testid={`article-expand-${article.id}`}
        >
          {expanded ? "Show less" : "Read full article"}
        </button>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="outline" onClick={copyArticle} data-testid={`article-copy-${article.id}`}>
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
          </Button>
          <Button size="sm" variant="outline" onClick={downloadArticle} data-testid={`article-download-${article.id}`}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download .md
          </Button>
          {article.hero_image_url && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => downloadMedia(article.hero_image_url, `xea-article-hero-${article.id.slice(0, 8)}`)}
              data-testid={`article-hero-download-${article.id}`}
            >
              <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Save image
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const ArticleStudioPage = () => {
  const { token, refreshUser } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };

  const [description, setDescription] = useState("");
  const [length, setLength] = useState("medium");
  const [tone, setTone] = useState("professional");
  const [withImage, setWithImage] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/articles`, { headers: auth });
        setArticles(r.data.items || []);
      } catch (_e) { /* non-fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async () => {
    if (description.trim().length < 10) {
      toast.error("Tell me a bit more about the company first.");
      return;
    }
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/articles/generate`,
        { description: description.trim(), length, tone, with_image: withImage },
        { headers: auth }
      );
      setArticles((prev) => [r.data.article, ...prev]);
      toast.success(`Article ready · ${r.data.tokens_after} XEA left`);
      if (r.data.note) toast.info(r.data.note);
      refreshUser?.();
    } catch (e) {
      toast.error(errMsg(e, "Article generation failed."));
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/articles/${id}`, { headers: auth });
      setArticles((prev) => prev.filter((a) => a.id !== id));
      toast.success("Article deleted");
    } catch (e) {
      toast.error(errMsg(e, "Could not delete."));
    }
  };

  const cost = 5 + (withImage ? 5 : 0);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-8" data-testid="article-studio-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-violet-500 flex items-center justify-center shadow-md">
          <Newspaper className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-violet-500">
            Article Studio
          </p>
          <h1 className="text-3xl md:text-4xl font-black text-ink-950 tracking-tight">
            Your company story, written for you.
          </h1>
        </div>
      </div>

      {/* AI assistant — helps draft the company brief */}
      <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50/40 to-white p-3">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-600 mb-2 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Ask XEA to help with your brief
        </p>
        <AssistantChat
          variant="embed"
          contextHint="Tell me about the company and I'll shape it into a strong article brief — angle, audience, what to highlight."
          usePromptLabel="Use as article brief"
          onUsePrompt={(text) => {
            setDescription(text.trim().slice(0, 3000));
            toast.success("Brief copied into the form below");
          }}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — form */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-ink-200 rounded-3xl p-6 space-y-5 lg:sticky lg:top-6">
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 block">
                What does the company do?
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                maxLength={3000}
                placeholder="We're a small-batch olive oil producer in Crete, family-run since 1974. We ship cold-pressed..."
                data-testid="article-description"
                className="w-full p-3 rounded-xl border border-ink-200 focus:outline-none focus:border-violet-500 text-sm resize-none"
              />
              <p className="text-[10px] text-ink-500 mt-1 text-right">{description.length}/3000</p>
              <p className="text-[11px] text-ink-500 mt-1">
                💡 If you've added products or store URLs in the Marketing realm, XEA weaves them in automatically.
              </p>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 block">
                Length
              </label>
              <div className="flex gap-2">
                {LENGTHS.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => setLength(l.id)}
                    data-testid={`article-length-${l.id}`}
                    className={`flex-1 p-2 rounded-xl border text-center transition ${
                      length === l.id
                        ? "border-violet-500 bg-violet-50 ring-2 ring-violet-300"
                        : "border-ink-200 bg-white hover:border-ink-400"
                    }`}
                  >
                    <div className="text-xs font-bold">{l.label}</div>
                    <div className="text-[10px] text-ink-500">{l.blurb}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 block">
                Tone
              </label>
              <div className="flex gap-2">
                {TONES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTone(t.id)}
                    data-testid={`article-tone-${t.id}`}
                    className={`flex-1 px-3 py-2 rounded-full text-xs font-bold border transition ${
                      tone === t.id
                        ? "bg-violet-500 text-white border-violet-500"
                        : "bg-white text-ink-700 border-ink-200 hover:border-violet-500"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setWithImage((v) => !v)}
              data-testid="article-with-image-toggle"
              className={`w-full p-3 rounded-xl border text-left transition flex items-center gap-3 ${
                withImage
                  ? "border-violet-500 bg-violet-50 ring-2 ring-violet-300"
                  : "border-ink-200 bg-white hover:border-ink-400"
              }`}
            >
              <ImageIcon className="w-4 h-4 text-violet-500 shrink-0" />
              <span>
                <span className="text-xs font-bold block">Include a hero image</span>
                <span className="text-[10px] text-ink-500">AI-generated 16:9 header (+5 XEA)</span>
              </span>
            </button>

            <Button
              onClick={handleGenerate}
              disabled={generating || description.trim().length < 10}
              data-testid="article-generate"
              className="w-full bg-violet-500 hover:bg-violet-600 text-white"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {generating ? "Writing…" : `Write my article · ${cost} XEA`}
            </Button>
          </div>
        </div>

        {/* Right — articles */}
        <div className="lg:col-span-2 space-y-4">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
            Your articles
          </p>
          {articles.length === 0 ? (
            <div className="rounded-3xl border-2 border-dashed border-ink-200 p-10 text-center bg-white">
              <Newspaper className="w-10 h-10 mx-auto text-ink-300 mb-3" />
              <p className="text-sm text-ink-500">
                Your articles will show up here — ready to copy, download, or publish.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {articles.map((a) => (
                <ArticleCard key={a.id} article={a} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArticleStudioPage;
